// System Map — per-sub-account change intelligence. Inventories the automation
// surface (workflows, forms, surveys, funnels, custom fields/values/objects) for
// a sub-account, and shows a change feed: who changed what, when. Backed by
// /ci/entities, /ci/ingest, /ci/changes, /ci/entity/:pk/versions.
import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  EntitySystemMap,
  EntityChange,
  EntityVersion,
  EntityType,
  SystemGraph,
  EntityHealth,
} from '../../lib/suite-types';
import { agencyApi } from '../../lib/agencyApi';
import { timeAgo } from '../../lib/time';
import type { ClientAssignment } from '../../lib/suite-types';
import { Button, Card, Chip, EmptyNote, ErrorNote, Select, Spinner, StatTile } from './ui';

// Dev fallback sub-account — the seeded demo location. Only offered when the agency has NO
// client assignments (i.e. local dev against seed data). A real agency's selector is built
// from its actual assignments; hard-wiring demo-loc here made the prod System Map query a
// location the agency doesn't have and render permanently empty.
const DEV_LOC = 'demo-loc';

// ── entity-type presentation ───────────────────────────────────────
// Partial: EntityType widened when Account Documentation landed; the `?? t` fallbacks below
// render any type this older map does not name.
const TYPE_LABEL: Partial<Record<EntityType, string>> = {
  workflow: 'Workflow',
  form: 'Form',
  survey: 'Survey',
  funnel: 'Funnel',
  custom_field: 'Custom field',
  custom_value: 'Custom value',
  custom_object: 'Custom object',
};
const TYPE_ORDER: EntityType[] = [
  'workflow', 'form', 'survey', 'funnel', 'custom_field', 'custom_value', 'custom_object',
];
const TYPE_ACCENT: Partial<Record<EntityType, string>> = {
  workflow: '#2563eb',
  form: '#9333ea',
  survey: '#0d9488',
  funnel: '#d97706',
  custom_field: '#0891b2',
  custom_value: '#65a30d',
  custom_object: '#db2777',
};
const typeLabel = (t: string) => TYPE_LABEL[t as EntityType] ?? t;
const typeAccent = (t: string) => TYPE_ACCENT[t as EntityType] ?? null;

const DASH = '—';

// ISO → coarse relative label ("just now", "3h ago", "2d ago", else date).
const fmtRelative = (iso: string | null): string => timeAgo(iso, { never: DASH });

export default function SystemMap() {
  // The sub-account picker is driven by the agency's REAL client assignments; the seeded
  // demo location only appears when there are none (local dev). null = assignments loading.
  const [subaccounts, setSubaccounts] = useState<ClientAssignment[] | null>(null);
  const [locationId, setLocationId] = useState<string | null>(null);
  const [map, setMap] = useState<EntitySystemMap | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    agencyApi.assignments()
      .then((a) => {
        if (cancelled) return;
        setSubaccounts(a);
        setLocationId(a[0]?.location_id ?? DEV_LOC);
      })
      .catch(() => {
        if (cancelled) return;
        setSubaccounts([]);
        setLocationId(DEV_LOC);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!locationId) return; // assignments still resolving
    setMap(null);
    setError(null);
    agencyApi
      .ciEntities(locationId)
      .then(setMap)
      .catch((e: Error) => setError(e.message));
  }, [locationId]);

  async function sync() {
    if (!locationId) return;
    setSyncing(true);
    setError(null);
    try {
      const m = await agencyApi.ciIngest(locationId);
      setMap(m);
      setReloadKey((k) => k + 1); // refresh the change feed against the new inventory
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSyncing(false);
    }
  }

  const tiles = useMemo(() => {
    const counts = map?.counts ?? {};
    return TYPE_ORDER.filter((t) => counts[t]).map((t) => ({
      type: t,
      label: typeLabel(t),
      value: counts[t],
    }));
  }, [map]);

  // Assignments (and therefore the initial sub-account) still resolving.
  if (!locationId) return <Spinner label="Loading sub-accounts…" />;

  return (
    <div className="space-y-5">
      {/* Sub-account selector + sync */}
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-slate-500">Sub-account</label>
        <Select value={locationId} onChange={setLocationId}>
          {(subaccounts ?? []).map((s) => (
            <option key={s.location_id} value={s.location_id}>
              {s.client_name || s.location_id}
            </option>
          ))}
          {/* Seed fallback only when the agency has no real assignments (local dev). */}
          {!subaccounts?.length && <option value={DEV_LOC}>Demo sub-account (demo-loc)</option>}
        </Select>
        <Button variant="secondary" onClick={sync} disabled={syncing} className="ml-auto">
          {syncing ? 'Syncing…' : '↻ Sync from HighLevel'}
        </Button>
      </div>

      {error && <ErrorNote error={error} />}
      {!map && !error && <Spinner label="Loading system map…" />}

      {map && (
        <>
          {/* Counts by type */}
          {tiles.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {tiles.map((t) => (
                <StatTile key={t.type} label={t.label} value={t.value} />
              ))}
            </div>
          ) : (
            <EmptyNote>
              No entities inventoried yet. Click <span className="font-medium">Sync from HighLevel</span> to
              pull this sub-account's automation surface.
            </EmptyNote>
          )}

          <div className="grid gap-5 lg:grid-cols-[1.4fr_1fr]">
            <EntityTable map={map} />
            <ChangeFeed locationId={locationId} reloadKey={reloadKey} />
          </div>
        </>
      )}

      {/* Doc-intel graph: health checklist + connections (independent of the inventory) */}
      <GraphPanel locationId={locationId} />
    </div>
  );
}

// ── Doc-intel graph (health checklist + connections) ───────────────
const HEALTH_META: Record<EntityHealth['severity'], { color: string; label: string }> = {
  error: { color: '#dc2626', label: 'Error' },
  warn: { color: '#d97706', label: 'Warning' },
  info: { color: '#2563eb', label: 'Info' },
};
const SEVERITY_ORDER: EntityHealth['severity'][] = ['error', 'warn', 'info'];
// Turn a health kind slug (missing_doc, broken_ref, …) into a readable label.
const humanize = (s: string) => s.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

function GraphPanel({ locationId }: { locationId: string }) {
  const [graph, setGraph] = useState<SystemGraph | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [busy, setBusy] = useState<null | 'import' | 'analyze'>(null);
  const [note, setNote] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setGraph(null);
    setError(null);
    agencyApi
      .ciGraph(locationId)
      .then(setGraph)
      .catch((e: Error) => setError(e.message));
  }, [locationId, reloadKey]);

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    file.text().then((txt) => setSnapshot(txt));
  }

  async function doImport() {
    setNote(null);
    setError(null);
    let payload: unknown;
    try {
      payload = JSON.parse(snapshot);
    } catch {
      setError('Snapshot is not valid JSON.');
      return;
    }
    setBusy('import');
    try {
      const g = await agencyApi.ciImport(payload);
      setGraph({ nodes: g?.nodes ?? [], edges: g?.edges ?? [], health: g?.health ?? [] });
      setSnapshot('');
      if (fileRef.current) fileRef.current.value = '';
      setNote('Snapshot imported.');
      setReloadKey((k) => k + 1);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function doAnalyze() {
    setNote(null);
    setError(null);
    setBusy('analyze');
    try {
      const g = await agencyApi.ciAnalyze(locationId);
      // Never hand a partial shape to the renderer — a missing `nodes` used to unmount the app.
      setGraph({ nodes: g?.nodes ?? [], edges: g?.edges ?? [], health: g?.health ?? [] });
      setNote('Analysis complete.');
      setReloadKey((k) => k + 1);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-700">Doc-intel graph</h3>
          <p className="text-xs text-slate-400">Health checks + how this sub-account's assets connect.</p>
        </div>
        <div className="flex items-center gap-3">
          {note && <span className="text-xs text-emerald-600">{note}</span>}
          <Button variant="secondary" onClick={doAnalyze} disabled={busy !== null}>
            {busy === 'analyze' ? 'Analyzing…' : '⚑ Analyze'}
          </Button>
        </div>
      </div>

      {/* Import snapshot */}
      <Card className="p-4">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Import snapshot
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            onChange={onFile}
            className="text-xs text-slate-500 file:mr-2 file:rounded-md file:border-0 file:bg-slate-100 file:px-2 file:py-1 file:text-xs file:text-slate-600 hover:file:bg-slate-200"
          />
        </div>
        <textarea
          value={snapshot}
          onChange={(e) => setSnapshot(e.target.value)}
          placeholder="Paste a GHL snapshot / location-export JSON, or choose a file…"
          rows={4}
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs text-slate-700 outline-none focus:border-brand focus:ring-1 focus:ring-brand"
        />
        <div className="mt-2 flex justify-end">
          <Button onClick={doImport} disabled={busy !== null || !snapshot.trim()}>
            {busy === 'import' ? 'Importing…' : 'Import snapshot'}
          </Button>
        </div>
      </Card>

      {error && <ErrorNote error={error} />}
      {!graph && !error && <Spinner label="Loading graph…" />}

      {graph && (
        <div className="grid gap-5 lg:grid-cols-[1fr_1.2fr]">
          <HealthChecklist health={graph.health} />
          <Connections graph={graph} />
        </div>
      )}
    </div>
  );
}

function HealthChecklist({ health }: { health: EntityHealth[] }) {
  const open = health.filter((h) => !h.resolved);
  const sorted = [...open].sort(
    (a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity),
  );
  return (
    <Card className="p-5">
      <h4 className="mb-1 text-sm font-semibold text-slate-700">Health checklist</h4>
      <p className="mb-3 text-xs text-slate-400">Issues found by the last analysis.</p>
      {sorted.length === 0 ? (
        <p className="text-sm text-emerald-600">✓ No open issues. Run Analyze to re-check.</p>
      ) : (
        <ul className="space-y-2.5">
          {sorted.map((h) => {
            const meta = HEALTH_META[h.severity] ?? HEALTH_META.info;
            return (
              <li key={h.id} className="flex items-start gap-2.5">
                <span
                  className="mt-1.5 h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: meta.color }}
                  aria-hidden
                />
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-slate-800">{humanize(h.kind)}</span>
                    <Chip label={meta.label} color={meta.color} />
                    {h.entity_pk && (
                      <span className="font-mono text-[11px] text-slate-400">{h.entity_pk}</span>
                    )}
                  </div>
                  {h.detail && <p className="mt-0.5 text-xs text-slate-500">{h.detail}</p>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function Connections({ graph }: { graph: SystemGraph }) {
  // name lookup by entity primary key (nodes.id === edge.from_pk / to_pk)
  const nameOf = useMemo(() => {
    const m = new Map(graph.nodes.map((n) => [n.id, n.name ?? 'Untitled']));
    return (pk: string | null) => (pk ? m.get(pk) ?? pk : null);
  }, [graph.nodes]);

  // group edges by source entity
  const grouped = useMemo(() => {
    const g = new Map<string, typeof graph.edges>();
    for (const e of graph.edges) {
      const arr = g.get(e.from_pk) ?? [];
      arr.push(e);
      g.set(e.from_pk, arr);
    }
    return [...g.entries()];
  }, [graph.edges]);

  return (
    <Card className="p-5">
      <h4 className="mb-1 text-sm font-semibold text-slate-700">
        Connections {graph.edges.length > 0 && <span className="text-slate-400">· {graph.edges.length}</span>}
      </h4>
      <p className="mb-3 text-xs text-slate-400">How assets reference each other.</p>
      {grouped.length === 0 ? (
        <p className="text-sm text-slate-400">No connections mapped yet. Import a snapshot to derive them.</p>
      ) : (
        <ul className="space-y-3">
          {grouped.map(([fromPk, edges]) => (
            <li key={fromPk} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
              <div className="text-sm font-medium text-slate-800">{nameOf(fromPk)}</div>
              <ul className="mt-1 space-y-1">
                {edges.map((e) => (
                  <li key={e.id} className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-600">
                      {e.relation}
                    </span>
                    <span className="text-slate-400">→</span>
                    <span className="text-slate-700">{nameOf(e.to_pk) ?? e.to_ref ?? '—'}</span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ── Entity inventory table ─────────────────────────────────────────
function EntityTable({ map }: { map: EntitySystemMap }) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const entities = map.entities;

  if (entities.length === 0) {
    return (
      <Card className="p-5">
        <h3 className="mb-3 text-sm font-semibold text-slate-700">Entities</h3>
        <p className="text-sm text-slate-400">Nothing inventoried for this sub-account.</p>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <div className="border-b border-slate-200 px-4 py-3">
        <h3 className="text-sm font-semibold text-slate-700">
          Entities <span className="ml-1 text-slate-400">· {entities.length}</span>
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Type</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 text-right font-medium">Ver.</th>
              <th className="px-4 py-3 text-right font-medium">Changes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {entities.map((e) => (
              <tr key={e.id} className="align-top hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                      className="text-left font-medium text-slate-800 hover:text-brand"
                      title="Show version timeline"
                    >
                      {e.name ?? <span className="text-slate-400">Untitled</span>}
                    </button>
                    {e.managed && <Chip label="managed" color="#16a34a" />}
                  </div>
                  {expanded === e.id && <VersionTimeline entityPk={e.id} />}
                </td>
                <td className="px-4 py-3">
                  <Chip label={typeLabel(e.entity_type)} color={typeAccent(e.entity_type)} />
                </td>
                <td className="px-4 py-3">
                  {e.status ? <Chip label={e.status} muted /> : <span className="text-slate-400">{DASH}</span>}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-slate-600">v{e.latest_version}</td>
                <td className="px-4 py-3 text-right tabular-nums text-slate-600">{e.change_count ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ── Version timeline (inline, under an expanded entity row) ────────
function VersionTimeline({ entityPk }: { entityPk: string }) {
  const [versions, setVersions] = useState<EntityVersion[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setVersions(null);
    setError(null);
    agencyApi
      .ciVersions(entityPk)
      .then(setVersions)
      .catch((e: Error) => setError(e.message));
  }, [entityPk]);

  if (error) return <p className="mt-2 text-xs text-rose-500">Couldn't load versions.</p>;
  if (!versions) return <p className="mt-2 text-xs text-slate-400">Loading versions…</p>;
  if (versions.length === 0) return <p className="mt-2 text-xs text-slate-400">No captured versions yet.</p>;

  return (
    <ol className="mt-2 space-y-1.5 border-l border-slate-200 pl-3">
      {versions.map((v) => (
        <li key={v.id} className="text-xs text-slate-500">
          <span className="font-medium text-slate-700">v{v.version}</span>
          {v.author_name ? <span> · {v.author_name}</span> : null}
          <span className="text-slate-400"> · {fmtRelative(v.captured_at)}</span>
        </li>
      ))}
    </ol>
  );
}

// ── Change feed (who changed what, when) ───────────────────────────
function ChangeFeed({ locationId, reloadKey }: { locationId: string; reloadKey: number }) {
  const [changes, setChanges] = useState<EntityChange[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setChanges(null);
    setError(null);
    agencyApi
      .ciChanges({ locationId })
      .then(setChanges)
      .catch((e: Error) => setError(e.message));
  }, [locationId, reloadKey]);

  return (
    <Card className="h-fit p-5">
      <h3 className="mb-1 text-sm font-semibold text-slate-700">Change feed</h3>
      <p className="mb-3 text-xs text-slate-400">Who changed what, when.</p>
      {error && <p className="text-sm text-rose-500">Couldn't load changes.</p>}
      {!changes && !error && <p className="text-sm text-slate-400">Loading…</p>}
      {changes && changes.length === 0 && (
        <p className="text-sm text-slate-400">No changes recorded yet.</p>
      )}
      {changes && changes.length > 0 && (
        <ul className="space-y-3">
          {changes.map((c) => (
            <ChangeRow key={c.id} change={c} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function ChangeRow({ change: c }: { change: EntityChange }) {
  const [open, setOpen] = useState(false);
  const diff = c.diff;
  const hasDiff = !!diff && (diff.added.length + diff.removed.length + diff.modified.length > 0);

  return (
    <li className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-slate-800">{c.entity_name ?? 'Untitled'}</span>
        <Chip label={typeLabel(c.entity_type)} color={typeAccent(c.entity_type)} />
        <span className="ml-auto shrink-0 text-xs text-slate-400">{fmtRelative(c.created_at)}</span>
      </div>
      <div className="mt-1 text-xs text-slate-500">
        <span className="font-medium text-slate-600">{c.author_name ?? 'Unknown'}</span>
        {c.from_version != null ? (
          <span className="text-slate-400"> · v{c.from_version} → v{c.to_version}</span>
        ) : (
          <span className="text-slate-400"> · v{c.to_version}</span>
        )}
      </div>
      {c.summary && <p className="mt-1 text-sm text-slate-700">{c.summary}</p>}
      {hasDiff && (
        <button
          onClick={() => setOpen((o) => !o)}
          className="mt-1 text-xs text-brand hover:underline"
        >
          {open ? 'Hide diff' : 'Show diff'}
        </button>
      )}
      {open && diff && (
        <div className="mt-2 space-y-1.5 rounded-lg bg-slate-50 p-3 text-xs">
          <DiffGroup label="Added" items={diff.added} color="text-emerald-600" sign="+" />
          <DiffGroup label="Removed" items={diff.removed} color="text-rose-600" sign="−" />
          <DiffGroup label="Modified" items={diff.modified} color="text-amber-600" sign="~" />
        </div>
      )}
    </li>
  );
}

function DiffGroup({ label, items, color, sign }: { label: string; items: string[]; color: string; sign: string }) {
  if (!items.length) return null;
  return (
    <div>
      <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</div>
      <ul className="mt-0.5 space-y-0.5">
        {items.map((it, i) => (
          <li key={`${it}-${i}`} className={`font-mono ${color}`}>
            <span className="select-none">{sign} </span>
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}
